import { Component, EventEmitter, Input, Output } from '@angular/core';
import { IonItem, IonLabel, IonSelect, IonSelectOption } from '@ionic/angular/standalone';
import { formatDuration } from '../../utils/date.utils';

/** Seconds: 30s … 10m inclusive, 30s steps (20 steps). */
export const QUEUE_SERVICE_TIMER_MIN_SECONDS = 30;
export const QUEUE_SERVICE_TIMER_MAX_SECONDS = 600;
export const QUEUE_SERVICE_TIMER_STEP_SECONDS = 30;

/** Sentinel `ion-select` value when the queue has no service timer alarm. */
export const QUEUE_SERVICE_TIMER_SELECT_OFF = 'off';

@Component({
  selector: 'crowdctrl-queue-service-timer-setting',
  standalone: true,
  imports: [IonItem, IonLabel, IonSelect, IonSelectOption],
  templateUrl: './queue-service-timer-setting.component.html',
})
export class QueueServiceTimerSettingComponent {
  @Input({ required: true }) label!: string;
  @Input() alarmEnabled = false;
  @Input() durationSeconds = QUEUE_SERVICE_TIMER_MIN_SECONDS;
  @Input() disabled = false;

  @Output() alarmEnabledChange = new EventEmitter<boolean>();
  @Output() durationSecondsChange = new EventEmitter<number>();

  readonly minSeconds = QUEUE_SERVICE_TIMER_MIN_SECONDS;
  readonly maxSeconds = QUEUE_SERVICE_TIMER_MAX_SECONDS;
  readonly stepSeconds = QUEUE_SERVICE_TIMER_STEP_SECONDS;

  readonly durationStepSeconds: number[] = (() => {
    const out: number[] = [];
    for (
      let s = QUEUE_SERVICE_TIMER_MIN_SECONDS;
      s <= QUEUE_SERVICE_TIMER_MAX_SECONDS;
      s += QUEUE_SERVICE_TIMER_STEP_SECONDS
    ) {
      out.push(s);
    }
    return out;
  })();

  get selectValue(): string {
    if (!this.alarmEnabled) {
      return QUEUE_SERVICE_TIMER_SELECT_OFF;
    }
    return String(this.snapSeconds(this.durationSeconds));
  }

  durationLabel(seconds: number): string {
    return formatDuration(this.snapSeconds(seconds) * 1000);
  }

  durationOptionValue(seconds: number): string {
    return String(seconds);
  }

  onSelectChange(event: CustomEvent<{ value: string | string[] }>): void {
    const raw = event.detail.value;
    const v = Array.isArray(raw) ? raw[0] : raw;
    if (v === QUEUE_SERVICE_TIMER_SELECT_OFF) {
      this.alarmEnabledChange.emit(false);
      return;
    }
    const n = Number(v);
    if (!Number.isFinite(n)) {
      return;
    }
    this.alarmEnabledChange.emit(true);
    this.durationSecondsChange.emit(this.snapSeconds(n));
  }

  private snapSeconds(v: number): number {
    const s = Math.round(v / this.stepSeconds) * this.stepSeconds;
    return Math.min(this.maxSeconds, Math.max(this.minSeconds, s));
  }
}
